import { blazePoseLandmarkName, KeyedFrameLandmarks } from "./clientPose";
import { MODEL_URL, WASM_ROOT } from "./mediapipeConfig";

/**
 * On-device pose extraction over an upload's SPLIT FRAMES, rather than over a video.
 *
 * The split PNGs are the right input for labelling an existing upload: they are the exact images
 * the labeller and renderer use, each carries a fileId so the result pairs exactly, and they are
 * served with permissive CORS so a crossOrigin image is not tainted. No seeking, no cadence
 * arithmetic, no assumption about where the clip was trimmed.
 *
 * A frame whose image cannot be loaded is RETRIED with a pause first — the split PNGs land on the
 * CDN one by one, and 17816 lost its last 0.9 s because the browser raced past frames that were
 * not there yet — and only omitted when it keeps failing (that frame then falls back to the
 * server); a frame where MediaPipe finds nobody keeps its slot with no keypoints.
 */

export type PoseImageFrame = { fileId: number; url: string };

export type ImagePoseResult = {
  captureWidth: number;
  captureHeight: number;
  frames: KeyedFrameLandmarks[];
};

/** Seams so the extraction is testable without a browser or a network. */
export type ImagePoseDeps = {
  loadImage: (url: string) => Promise<{ image: unknown; width: number; height: number }>;
  createLandmarker: () => Promise<{
    detect: (image: unknown) => { landmarks?: Array<Array<{ x: number; y: number; z?: number; visibility?: number }>> };
    close: () => void;
  }>;
};

async function defaultLoadImage(url: string) {
  const image = new Image();
  // Without this the canvas/GL upload taints and detection throws SecurityError. The uploads
  // store answers with Access-Control-Allow-Origin: *, so the request is not credentialed.
  image.crossOrigin = "anonymous";
  image.src = url;
  await image.decode();
  return { image, width: image.naturalWidth, height: image.naturalHeight };
}

async function defaultCreateLandmarker() {
  const { FilesetResolver, PoseLandmarker } = await import("@mediapipe/tasks-vision");
  const fileset = await FilesetResolver.forVisionTasks(WASM_ROOT);
  const landmarker = await PoseLandmarker.createFromOptions(fileset, {
    baseOptions: { modelAssetPath: MODEL_URL },
    runningMode: "IMAGE",
    numPoses: 1,
  });
  return {
    detect: (image: unknown) => landmarker.detect(image as HTMLImageElement),
    close: () => landmarker.close(),
  };
}

export const defaultImagePoseDeps: ImagePoseDeps = {
  loadImage: defaultLoadImage,
  createLandmarker: defaultCreateLandmarker,
};

export type ImagePoseRetry = {
  /** Load attempts per frame (default 3). */
  retries?: number;
  /** Pause between attempts (default 1500 ms — CDN propagation, not a flaky network). */
  retryDelayMs?: number;
  sleep?: (ms: number) => Promise<void>;
};

const defaultSleep = (ms: number) => new Promise<void>((resolve) => window.setTimeout(resolve, ms));

export async function extractPoseFromImages(
  frames: PoseImageFrame[],
  onProgress: (done: number, total: number) => void,
  deps: ImagePoseDeps = defaultImagePoseDeps,
  retry: ImagePoseRetry = {}
): Promise<ImagePoseResult> {
  const attempts = Math.max(1, retry.retries ?? 3);
  const delayMs = retry.retryDelayMs ?? 1500;
  const sleep = retry.sleep ?? defaultSleep;
  const loadWithRetry = async (url: string) => {
    let lastError: unknown;
    for (let attempt = 1; attempt <= attempts; attempt++) {
      try {
        return await deps.loadImage(url);
      } catch (e) {
        lastError = e;
        if (attempt < attempts) await sleep(delayMs);
      }
    }
    throw lastError;
  };
  const landmarker = await deps.createLandmarker();
  const out: KeyedFrameLandmarks[] = [];
  let captureWidth = 0;
  let captureHeight = 0;

  try {
    for (let i = 0; i < frames.length; i++) {
      const frame = frames[i];
      try {
        const loaded = await loadWithRetry(frame.url);
        if (captureWidth === 0) {
          captureWidth = loaded.width;
          captureHeight = loaded.height;
        } else if (loaded.width !== captureWidth || loaded.height !== captureHeight) {
          // One pose file carries ONE capture size; mixed sizes would scale most frames wrongly.
          throw new Error(
            `frame ${frame.fileId} is ${loaded.width}x${loaded.height}, expected ${captureWidth}x${captureHeight}`
          );
        }

        const detected = landmarker.detect(loaded.image);
        const landmarks = (detected.landmarks?.[0] ?? [])
          .map((p, index) => ({ index, x: p.x, y: p.y, z: p.z, visibility: p.visibility }))
          .filter((p) => blazePoseLandmarkName(p.index) !== undefined);
        out.push({ fileId: frame.fileId, landmarks });
      } catch (e) {
        // Mixed sizes are a contract break, not a bad frame — surface it.
        if (e instanceof Error && e.message.includes("expected")) throw e;
        // eslint-disable-next-line no-console
        console.warn(`[clientLabels] frame ${frame.fileId} skipped:`, e);
      }
      onProgress(i + 1, frames.length);
    }
  } finally {
    landmarker.close();
  }

  return { captureWidth, captureHeight, frames: out };
}
